import type WpiBase from "./wpi.base.ts";
import {print} from "../config.ts";

export default class WpiiOS implements WpiBase {
  counts: number = 0;

  callbacks: Record<string, { resolve: (v: any) => void; timer?: ReturnType<typeof setTimeout> }> = {};

  constructor() {
    window.addEventListener("message", (event) => this.response(event));
  }

  private isAvailable(): boolean {
    return !!window.webkit?.messageHandlers?.Interface?.postMessage;
  }

  /**
   * @param action
   * @param payload
   * @param callback  true면 응답 기다림, false면 fire-and-forget
   * @param timeoutMs 응답 대기 제한(ms)
   */
  call(action: any, payload: any = null, callback: boolean = true, timeoutMs: number = 5000): Promise<any> {
    const cbid = "cb_" + (++this.counts);

    return new Promise((resolve) => {
      const doneFail = (error: string, detail?: any) => {
        const cb = this.callbacks[cbid];
        if (cb?.timer) clearTimeout(cb.timer);
        delete this.callbacks[cbid];
        resolve({ ok: false, error, detail, cbid, action });
      };

      if (!this.isAvailable()) {
        doneFail("NO_IOS_HANDLER");
        return;
      }

      // callback 안 받을거면: 전송만 시도하고 바로 resolve
      if (!callback) {
        try {
          window.webkit.messageHandlers.Interface.postMessage(
            JSON.stringify({ cbid, action, payload })
          );
          resolve({ ok: true, cbid, action });
        } catch (e) {
          doneFail("POSTMESSAGE_FAILED", String(e));
        }
        return;
      }

      // callback 받을거면 등록 + timeout
      this.callbacks[cbid] = { resolve };

      this.callbacks[cbid].timer = setTimeout(() => {
        doneFail("TIMEOUT");
      }, timeoutMs);

      try {
        window.webkit.messageHandlers.Interface.postMessage(
          JSON.stringify({ cbid, action, payload })
        );
      } catch (e) {
        doneFail("POSTMESSAGE_FAILED", String(e));
      }
    });
  }

  response(event: any) {
    let json: any = event && event.data;
    if (json == null) return;

    if (typeof json === "string") {
      try { json = JSON.parse(json); } catch { return; }
    }

    const cbid = json?.cbid;
    if (!cbid) return;

    const cb = this.callbacks[cbid];
    if (!cb) return;

    if (cb.timer) clearTimeout(cb.timer);
    delete this.callbacks[cbid];

    // 기존 형태 유지: {cbid, ...response}
    const { cbid: _, ...response } = json;
    cb.resolve.call(null, response);
  }

  async version(): Promise<string> {
    const response = await this.call(this.version.name, null, true, 100);
    return (response && response.version) || "unknown";
  }

  async notification(title: string, content: string, subtitle?: string): Promise<void> {
    await this.call(this.notification.name, { title, content, subtitle }, false);
  }

  async media_session(title: string, artist: string, album: string, image: string): Promise<void> {
    await this.call(this.media_session.name, { title, artist, album, image }, false);
  }

  // 현재 ios메소드가 없으므로 현재는 바로 타임아웃 설정
  async media_uri(file: string): Promise<string | null> {
    // const response = await this.call(this.media_uri.name, { file }, true, 50);
    // return response?.uri || null;
    print(file);
    return null;
  }

  async media_save(file: string, length: string, url: string): Promise<boolean> {
    // const response = await this.call(this.media_save.name, { file, length, url }, true, 50);
    // return !!response?.result;
    print(file, length, url);
    return false;
  }

  async media_delete(file: string): Promise<boolean> {
    // const response = await this.call(this.media_delete.name, { file }, true, 50);
    // return !!response?.result;
    print(file);
    return false;
  }

  async save_begin(key: string, len: number): Promise<void> {
    print('WpiiOS.save_begin', key, len);
  }

  async save_chunk(key: string, index: number, chunk: string): Promise<void> {
    print('WpiiOS.save_chunk', key, index, chunk);
  }

  async save_end(key: string): Promise<void> {
    print('WpiiOS.save_end', key);
  }

  async network_check(fn: string, server: string): Promise<void> {
    print('WpiDefault.network_check', fn, server);
  }
}
